import { useState, useCallback } from 'react';
import { Projet, SituationTravaux, TravauxSupplementaires, Incident, SituationSousTraitant } from '../types';
import { projetsInitiaux } from '../data/mockData';

export type VueActive = 'dashboard' | 'suivi-chantier';

export function useSuiviProjetsState() {
  const [projets, setProjets] = useState<Projet[]>(projetsInitiaux);
  const [vueActive, setVueActive] = useState<VueActive>('dashboard');
  const [projetSelectionneId, setProjetSelectionneId] = useState<string | null>(null);

  const projetSelectionne = projets.find(p => p.id === projetSelectionneId) || null;

  const ouvrirSuiviChantier = useCallback((projetId: string) => {
    setProjetSelectionneId(projetId);
    setVueActive('suivi-chantier');
  }, []);

  const retourDashboard = useCallback(() => {
    setVueActive('dashboard');
    setProjetSelectionneId(null);
  }, []);

  const ajouterSituation = useCallback((projetId: string, lotId: string, situation: Omit<SituationTravaux, 'id'>) => {
    setProjets(prev => prev.map(p => {
      if (p.id !== projetId) return p;
      return {
        ...p,
        lots: p.lots.map(l => {
          if (l.id !== lotId) return l;
          const newSituation: SituationTravaux = {
            ...situation,
            id: `s-${Date.now()}`,
          };
          return {
            ...l,
            situations: [...l.situations, newSituation],
            statut: l.statut === 'non_demarre' ? 'en_cours' : l.statut,
          };
        }),
      };
    }));
  }, []);

  const ajouterTravauxSupplementaires = useCallback((
    projetId: string,
    lotId: string,
    ts: Omit<TravauxSupplementaires, 'id' | 'lotId'>
  ) => {
    setProjets(prev => prev.map(p => {
      if (p.id !== projetId) return p;
      return {
        ...p,
        lots: p.lots.map(l => {
          if (l.id !== lotId) return l;
          const newTS: TravauxSupplementaires = {
            ...ts,
            id: `ts-${Date.now()}`,
            lotId,
          };
          return {
            ...l,
            travauxSupplementaires: [...l.travauxSupplementaires, newTS],
          };
        }),
      };
    }));
  }, []);

  const ajouterIncident = useCallback((
    projetId: string,
    incident: Omit<Incident, 'id' | 'projetId'>
  ) => {
    setProjets(prev => prev.map(p => {
      if (p.id !== projetId) return p;
      const newIncident: Incident = {
        ...incident,
        id: `inc-${Date.now()}`,
        projetId,
      };
      return {
        ...p,
        incidents: [...p.incidents, newIncident],
      };
    }));
  }, []);

  const resoudreIncident = useCallback((projetId: string, incidentId: string) => {
    setProjets(prev => prev.map(p => {
      if (p.id !== projetId) return p;
      return {
        ...p,
        incidents: p.incidents.map(inc => {
          if (inc.id !== incidentId) return inc;
          return {
            ...inc,
            statut: 'resolu' as const,
            dateResolution: new Date().toISOString().split('T')[0],
          };
        }),
      };
    }));
  }, []);

  const ajouterSituationSousTraitant = useCallback((
    projetId: string,
    lotId: string,
    situation: Omit<SituationSousTraitant, 'id'>
  ) => {
    setProjets(prev => prev.map(p => {
      if (p.id !== projetId) return p;
      return {
        ...p,
        lots: p.lots.map(l => {
          if (l.id !== lotId || !l.sousTraitant) return l;
          const newSituation: SituationSousTraitant = {
            ...situation,
            id: `sst-${Date.now()}`,
          };
          return {
            ...l,
            sousTraitant: {
              ...l.sousTraitant,
              situationsSousTraitant: [
                ...l.sousTraitant.situationsSousTraitant,
                newSituation,
              ],
            },
          };
        }),
      };
    }));
  }, []);

  return {
    projets,
    vueActive,
    projetSelectionne,
    projetSelectionneId,
    ouvrirSuiviChantier,
    retourDashboard,
    ajouterSituation,
    ajouterTravauxSupplementaires,
    ajouterIncident,
    resoudreIncident,
    ajouterSituationSousTraitant,
  };
}
